import SignAgreementPopup, { Signature } from './SignAgreementPopup'
import useZoom, { MAX_SCALE, MIN_SCALE } from './hooks/useZoom'
import React, { useEffect, useRef, useState } from 'react'
import ArrowBtn from 'components/buttons/ArrowBtn'
import useTranslation from 'hooks/useTranslation'
import { checkIsSmallScreen } from 'utils/screen'
import usePopupState from 'hooks/usePopupState'
import { Document, Page } from 'react-pdf'
import Loading from 'components/Loading'

import 'react-pdf/dist/Page/AnnotationLayer.css'
import 'react-pdf/dist/Page/TextLayer.css'
import './style.scss'

interface ContractProps {
  title?: React.JSX.Element
  isLoading?: boolean
  contractPdf: string
  contractGroupId: string
  signature?: Signature | false
  requiresNewSignature?: boolean
  modalKey?: string
  outsideCart?: boolean
  /** Sam podgląd PDF (bez podpisywania) - przycisk tylko zamyka */
  readOnly?: boolean
  callback: basic.BasicFunc
  signAgreementCallback?: (signature: Signature, replacedBy?: string) => void
}

const Contract: React.FC<ContractProps> = ({ title, isLoading, contractPdf, contractGroupId, modalKey = 'signAgreement', signature, requiresNewSignature, outsideCart, readOnly = false, callback, signAgreementCallback }) => {
  const [totalPages, setTotalPages] = useState(0)
  const [width, setWidth] = useState(window.innerWidth)
  const [isSignButton, showSignButton] = useState(false)
  const { popup, openPopup } = usePopupState<{ type: 'signAgreement' }>()
  const { scale, onTouchStart, onDbClick, onWheel, zoomIn, zoomOut } = useZoom()
  const { t } = useTranslation('contract')

  const mainRef = useRef<HTMLDivElement>(null)
  const scrollToBottom = useRef(false)


  useEffect(() => {
    if (signature && scrollToBottom.current) {
      scrollToBottom.current = false
      const main = mainRef.current
      if (main) {
        const top = main.scrollHeight - main.clientHeight + 1
        mainRef.current.scrollTo({ top, behavior: 'smooth' })
      }
    }
  }, [signature])

  useEffect(() => {
    if (totalPages === 1) {
      const timeout = setTimeout(() => {
        const main = mainRef.current
        if (main && main.scrollHeight === main.clientHeight) {
          showSignButton(true)
        }
      })
      return () => {
        clearTimeout(timeout)
      }
    }
  }, [totalPages])

  useEffect(() => {
    if (isLoading) return
    const updateWidth = () => {
      if (mainRef.current) {
        setWidth(mainRef.current.clientWidth - (checkIsSmallScreen() ? 0 : 42))
      }
    }

    updateWidth()
    window.addEventListener('resize', updateWidth)
    return () => {
      window.removeEventListener('resize', updateWidth)
    }
  }, [isLoading])

  const buttonText = (() => {
    if (readOnly) return t('close')
    if (!isSignButton) return t('swipeToContinue')
    return signature ? t('continue') : t('signAgreement')
  })()

  return (
    <>
      {!readOnly && popup?.type === modalKey && contractGroupId && <SignAgreementPopup
        outsideCart={outsideCart}
        contractGroupId={contractGroupId}
        callback={(_signature, replacedBy) => {
          scrollToBottom.current = true
          signAgreementCallback?.(_signature, replacedBy)
        }}
      />}

      <div className='orderContract'>
        {title}
        {!isLoading && requiresNewSignature && !signature && <p className='--error'>{t('requiresNewSignature')}</p>}
        {!isLoading
          ? <div
            ref={mainRef}
            className='orderContract__main'
            onScroll={() => {
              const main = mainRef.current
              if (!main) return
              if ((main.clientHeight + main.scrollTop + 1) >= main.scrollHeight) {
                showSignButton(true)
              } else if (isSignButton) {
                showSignButton(false)
              }
            }}>

            {totalPages > 0 && <div className='orderContract__zoom__wrapper'>
              <div className='orderContract__zoom'>
                {scale < MAX_SCALE && <i
                  className='icon icon--zoom-in s32'
                  onClick={zoomIn} />}
                {scale > MIN_SCALE && <i
                  className='icon icon--zoom-out s32'
                  onClick={zoomOut} />}
              </div>
            </div>}

            {contractPdf && <div
              onWheel={onWheel}
              onDoubleClick={onDbClick}
              onTouchStart={onTouchStart}>
              <Document
                file={contractPdf}
                loading={<Loading middleOfPage={modalKey === 'signAgreement'} />}
                error={<div className='orderContract__error'>
                  <p>{t('pdfLoadFailed')}</p>
                  <span
                    className='--link'
                    onClick={() => {
                      window.open(contractPdf)
                    }}>
                    {t('openInNewTab')}
                  </span>
                </div>}
                onLoadSuccess={({ numPages }) => {
                  setTotalPages(numPages)
                }}>
                {Array.from(new Array(totalPages), (_, i) => <Page
                  key={i}
                  width={width}
                  pageNumber={i + 1}
                  scale={scale}
                  renderTextLayer={false}
                  renderAnnotationLayer={false}
                />)}
              </Document>
            </div>}

            {signature && <div className='orderContract__confirmation'>
              <p>{t('agreementAcceptanceConfirmation')}</p>
              <div className='orderContract__confirmation__list'>
                <div className='orderContract__confirmation__list__item'>
                  <span>{t('fullName')}: </span>
                  <span><b>{signature.name}</b></span>
                </div>
                <div className='orderContract__confirmation__list__item'>
                  <span>{t('acceptanceMethod')}: </span>
                  <span><b>{signature.type}</b></span>
                </div>
                <div className='orderContract__confirmation__list__item'>
                  <span>{t('acceptanceDateTime')}: </span>
                  <span><b>{signature.date}</b></span>
                </div>
                {(signature.device || signature.email || signature.phone) && <div className='orderContract__confirmation__list__item'>
                  <span>{t(signature.email ? 'emailUsedForAcceptance' : 'phoneNumberUsedForAcceptance')}: </span>
                  <span><b>{signature.device || signature.email || signature.phone}</b></span>
                </div>}
                {signature.ip && <div className='orderContract__confirmation__list__item'>
                  <span>{t('ipAddress')}: </span>
                  <span><b>{signature.ip}</b></span>
                </div>}
              </div>
            </div>}
          </div>
          : <div className='orderContract__main --basic'>
            <Loading />
          </div>}

        <ArrowBtn
          enlarged
          disabled={isLoading}
          text={buttonText}
          action={() => {
            if (readOnly) {
              callback()

            } else if (isSignButton) {
              if (signature) {
                callback()
              } else {
                openPopup({ type: modalKey, props: { contractGroupId, contractPdf } })
              }

            } else {
              const main = mainRef.current
              if (!main) return
              const maxTop = main.scrollHeight - main.clientHeight + 1
              const potentialNextTop = main.scrollTop + main.clientHeight
              const nextTop = Math.min(maxTop, potentialNextTop)
              main.scrollTo({ top: nextTop, behavior: 'smooth' })
            }
          }}
        />
      </div>
    </>
  )
}

export default Contract